// ═══════════════════════════════════════════════════════════════
// filesApi.js — Client HTTP pour FileOrganizer S59
// ═══════════════════════════════════════════════════════════════
//
// Expose les 4 endpoints de la feature "Organiser les fichiers avec l'IA" :
// propose / apply / rollback / history.
// ═══════════════════════════════════════════════════════════════

const getToken = () => localStorage.getItem('oracle_token');

export class FilesApiError extends Error {
    constructor(status, message, detail) {
        super(message || 'Erreur API files');
        this.name = 'FilesApiError';
        this.status = status;
        this.detail = detail;
    }
}

async function request(path, { method = 'GET', body = null, timeoutMs = 30000 } = {}) {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), timeoutMs);
    try {
        const res = await fetch(path, {
            method,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${getToken()}`,
            },
            body: body ? JSON.stringify(body) : undefined,
            signal: ctl.signal,
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
            throw new FilesApiError(res.status, data.error || data.message || `HTTP ${res.status}`, data);
        }
        return data;
    } finally {
        clearTimeout(timer);
    }
}

// S59 — File Organizer : propose / apply / rollback / history

/**
 * Lance l'analyse IA pour proposer une organisation.
 * @param {number} projectId
 * @param {Object} options
 * @param {'haiku'|'kimi'|'sonnet'|'opus'} [options.modelTier]
 * @param {boolean} [options.preserveExisting] — défaut true côté serveur
 * @param {Object|null} [options.scope] — { folderId } pour limiter à un dossier
 * @returns {Promise<Object>} plan
 */
export function organizeProposeFiles(projectId, { modelTier, preserveExisting, scope } = {}) {
    return request(`/api/projects/${projectId}/files/organize/propose`, {
        method: 'POST',
        body: {
            model_tier: modelTier,
            preserve_existing: preserveExisting,
            scope: scope || undefined,
        },
        timeoutMs: 120000,  // Kimi peut prendre 30-60s sur gros projets
    });
}

/**
 * Applique un plan édité (le plan peut différer du propose initial —
 * l'utilisateur a pu retirer des moves, supprimer des doublons, etc.).
 * @param {number} projectId
 * @param {Object} plan — structure retournée par propose, potentiellement modifiée
 * @param {number[]} [plan.duplicates_to_delete] — ids de fichiers doublons à hard-delete
 */
export function organizeApplyFiles(projectId, plan) {
    return request(`/api/projects/${projectId}/files/organize/apply`, {
        method: 'POST',
        body: { plan },
        timeoutMs: 60000,
    });
}

/**
 * Annule une organisation appliquée (dans la fenêtre 72h).
 */
export function organizeRollback(projectId, logId) {
    return request(`/api/projects/${projectId}/files/organize/rollback/${logId}`, {
        method: 'POST',
        timeoutMs: 30000,
    });
}

/**
 * Historique des dernières organisations du projet (avec flag can_rollback).
 */
export function organizeHistory(projectId, { limit = 10 } = {}) {
    return request(`/api/projects/${projectId}/files/organize/history?limit=${limit}`);
}
